// Considered: DataListMolecule (composed below) — no existing list molecule combines
// per-row inline numeric editing (weight, an edge-scoped value) with per-row status;
// this molecule composes DataListMolecule + StatusBadgeAtom/ChipAtom/ButtonAtom/DialogWrapper
// rather than duplicating table plumbing. See spec Reuse Audit (EAINT-696).
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import type { StatusValue } from 'types/spherePoints';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import DataListMolecule from '../DataList/DataListMolecule';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import './MeasureMembersListMolecule.scss';

export interface MeasureMember {
  sphereId: string;
  name: string;
  status: StatusValue | null;
  weight: number;
  linkedVia: 'deploy' | 'backfill' | 'automation' | 'membership' | 'manual';
  memberRole: 'instance' | 'aggregate';
}

interface MeasureMembersResponse {
  measureSystemName: string | null;
  members: MeasureMember[];
}

export interface MeasureMembersListMoleculeProps {
  /** The aggregate sphere whose measures_into members are listed. */
  aggregateSphereId: string;
  /** Caller has edit on the aggregate — controls whether weight/unlink actions render. */
  canEdit?: boolean;
  className?: string;
}

const MeasureMembersListMolecule: React.FC<MeasureMembersListMoleculeProps> = ({
  aggregateSphereId,
  canEdit = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [members, setMembers] = useState<MeasureMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weightDrafts, setWeightDrafts] = useState<Record<string, string>>({});
  const [savingSphereId, setSavingSphereId] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<MeasureMember | null>(null);

  const loadMembers = useCallback(async () => {
    if (!apiClients) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClients.graphClient.get<MeasureMembersResponse>(
        `/spheres/${encodeURIComponent(aggregateSphereId)}/measure-members`
      );
      setMembers(response.members);
    } catch {
      setError(t('measureMembers.list.error'));
    } finally {
      setLoading(false);
    }
    // `t` only formats error copy here; including it would re-fire the fetch
    // whenever the translation function's identity changes across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients, aggregateSphereId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleSaveWeight = useCallback(
    async (member: MeasureMember) => {
      if (!apiClients) return;
      const draft = weightDrafts[member.sphereId];
      // A cleared field is an empty string, not undefined; Number('') is 0, which
      // would silently drop the member from the rollup. Treat empty/blank as invalid.
      let weight: number;
      if (draft === undefined) {
        weight = member.weight;
      } else if (draft.trim() === '') {
        weight = Number.NaN;
      } else {
        weight = Number(draft);
      }
      if (!Number.isFinite(weight) || weight < 0) {
        setError(t('measureMembers.weightEditor.error'));
        return;
      }

      setSavingSphereId(member.sphereId);
      setError(null);
      try {
        await apiClients.graphClient.patch(
          `/spheres/${encodeURIComponent(aggregateSphereId)}/measure-members/${encodeURIComponent(member.sphereId)}`,
          { weight }
        );
        setMembers((prev) =>
          prev.map((m) => (m.sphereId === member.sphereId ? { ...m, weight } : m))
        );
        setWeightDrafts((prev) => {
          const next = { ...prev };
          delete next[member.sphereId];
          return next;
        });
      } catch {
        setError(t('measureMembers.weightEditor.error'));
      } finally {
        setSavingSphereId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see loadMembers above
    [apiClients, aggregateSphereId, weightDrafts]
  );

  const handleConfirmUnlink = useCallback(async () => {
    if (!apiClients || !unlinkTarget) return;
    const target = unlinkTarget;
    setUnlinkTarget(null);
    setError(null);
    try {
      await apiClients.graphClient.delete(
        `/spheres/${encodeURIComponent(aggregateSphereId)}/measure-members/${encodeURIComponent(target.sphereId)}`
      );
      setMembers((prev) => prev.filter((m) => m.sphereId !== target.sphereId));
    } catch {
      setError(t('measureMembers.row.unlinkError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see loadMembers above
  }, [apiClients, aggregateSphereId, unlinkTarget]);

  const columns: DataGridColumn[] = [
    {
      field: 'name',
      header: t('measureMembers.list.nameColumn'),
      body: (row: MeasureMember) => (
        <div className="measure-members-list__name-cell">
          {row.status !== null && <StatusBadgeAtom status={row.status} size={16} />}
          <span>{row.name}</span>
        </div>
      ),
    },
    {
      field: 'weight',
      header: t('measureMembers.list.weightColumn'),
      body: (row: MeasureMember) =>
        canEdit ? (
          <div className="measure-members-list__weight-cell">
            <input
              type="number"
              min={0}
              step="0.1"
              className="measure-members-list__weight-input"
              value={weightDrafts[row.sphereId] ?? String(row.weight)}
              onChange={(e) =>
                setWeightDrafts((prev) => ({ ...prev, [row.sphereId]: e.target.value }))
              }
              aria-label={t('measureMembers.list.weightColumn')}
              data-testid={`measure-member-weight-input-${row.sphereId}`}
            />
            <ButtonAtom
              type="button"
              variant="text"
              shape="pill"
              size="small"
              label={t('measureMembers.weightEditor.save')}
              disabled={savingSphereId === row.sphereId}
              onClick={() => handleSaveWeight(row)}
              data-testid={`measure-member-weight-save-${row.sphereId}`}
            />
          </div>
        ) : (
          <span>{row.weight}</span>
        ),
    },
    {
      field: 'linkedVia',
      header: t('measureMembers.list.linkedViaColumn'),
      body: (row: MeasureMember) => (
        <ChipAtom label={t(`measureMembers.row.linkedVia.${row.linkedVia}`)} />
      ),
    },
    ...(canEdit
      ? [
          {
            field: 'actions',
            header: '',
            body: (row: MeasureMember) =>
              row.linkedVia === 'membership' ? null : (
                <ButtonAtom
                  type="button"
                  variant="danger"
                  shape="pill"
                  size="small"
                  label={t('measureMembers.row.unlink')}
                  onClick={() => setUnlinkTarget(row)}
                  data-testid={`measure-member-unlink-${row.sphereId}`}
                />
              ),
          } as DataGridColumn,
        ]
      : []),
  ];

  const classes = ['measure-members-list-molecule', className].filter(Boolean).join(' ');

  return (
    <section className={classes} data-testid="measure-members-list">
      <DataListMolecule<MeasureMember>
        data={members}
        columns={columns}
        loading={loading}
        error={error}
        emptyMessage={t('measureMembers.list.empty')}
        dataKey="sphereId"
        title={t('measureMembers.list.title')}
        showRefresh
        onRefresh={loadMembers}
      />

      {unlinkTarget && (
        <DialogWrapper
          header={t('measureMembers.unlinkConfirm.title')}
          visible={Boolean(unlinkTarget)}
          onHide={() => setUnlinkTarget(null)}
        >
          <p>{t('measureMembers.unlinkConfirm.message', { name: unlinkTarget.name })}</p>
          <div className="measure-members-list__dialog-actions">
            <ButtonAtom
              type="button"
              variant="secondary"
              shape="pill"
              size="small"
              label={t('measureMembers.unlinkConfirm.cancel')}
              onClick={() => setUnlinkTarget(null)}
            />
            <ButtonAtom
              type="button"
              variant="danger"
              shape="pill"
              size="small"
              label={t('measureMembers.unlinkConfirm.confirm')}
              onClick={handleConfirmUnlink}
              data-testid="measure-member-unlink-confirm"
            />
          </div>
        </DialogWrapper>
      )}
    </section>
  );
};

export default MeasureMembersListMolecule;
